/* eslint-disable no-restricted-syntax */

/**
 * A form group can either define its fields inline or pull them from a reusable field set
 * (`api::form-field-set.form-field-set`), which is how the identical contact step is shared
 * across all forms.
 *
 * Every downstream consumer — `getInitialFormValues`, `getFormDisplayState`,
 * `getValidationSchema`, `getDisplayValues` — iterates `group.fields`. Resolving the field
 * set into that same array once, right after the GraphQL result arrives, keeps all of them
 * unchanged.
 *
 * When a group has both, the field set comes first and the inline fields are appended, so a
 * form can extend the shared step with one extra question.
 */
function normalizeForm(form) {
  if (!form) {
    return form;
  }

  const seenFieldIds = new Set();

  const items = (form.items ?? []).map((group) => {
    const fieldSetFields = group.fieldSet?.data?.attributes?.fields ?? [];
    const inlineFields = group.fields ?? [];

    if (fieldSetFields.length === 0) {
      collectIds(inlineFields, seenFieldIds, group);
      return group;
    }

    const fields = [...fieldSetFields, ...inlineFields];
    collectIds(fields, seenFieldIds, group);

    return {
      ...group,
      fields,
    };
  });

  return {
    ...form,
    items,
  };
}

/**
 * Formik state is keyed `field_<id>` off the Strapi component row id, which is globally
 * unique — unless the same field set is referenced by two groups of one form, in which case
 * both groups would write to the same keys. Warn rather than silently misbehave; using a
 * field set twice in one form is not supported.
 */
function collectIds(fields, seenFieldIds, group) {
  for (const field of fields) {
    if (seenFieldIds.has(field.id)) {
      console.warn(
        `[form] Duplicate field id ${field.id} ("${field.name}") in group "${group.title ?? group.id}". `
        + 'A field set must not be referenced more than once per form — the duplicated fields share state.',
      );
    }

    seenFieldIds.add(field.id);
  }
}

module.exports = normalizeForm;
